/**
 * clone_flow tool - Clone an existing Power Automate flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { ConnectionReference, CreateFlowRequest } from "../api/types.js";
import { logger } from "../utils/logger.js";

export const cloneFlowInputSchema = z.object({
  sourceFlowId: z.string().describe("The flow ID (GUID) to clone"),
  newDisplayName: z.string().describe("Display name for the cloned flow"),
  targetEnvironment: z
    .string()
    .optional()
    .describe("Target environment for the clone. Defaults to same environment as source."),
  sourceEnvironment: z
    .string()
    .optional()
    .describe("Source environment. Uses default if omitted."),
  updateConnections: z
    .record(
      z.string(),
      z.object({
        connectionName: z.string(),
        id: z.string(),
        source: z.enum(["Invoker", "Embedded"]).optional(),
        tier: z.enum(["Standard", "Premium", "NotSpecified"]).optional(),
      })
    )
    .optional()
    .describe(
      "New connection references for the clone, keyed by the SOURCE flow's connection reference keys " +
      "(e.g. 'shared_office365'). Each value is an object: { connectionName: '<connection name/id from " +
      "list_connections>', id: '/providers/Microsoft.PowerApps/apis/<connector>', source?, tier? }."
    ),
});

export type CloneFlowInput = z.infer<typeof cloneFlowInputSchema>;

export const cloneFlowTool = {
  name: "clone_flow",
  description:
    "Clone an existing Power Automate flow to create a copy with a new name. Optionally update connection references in the clone. The cloned flow will be created in a stopped state.",
  inputSchema: {
    type: "object" as const,
    properties: {
      sourceFlowId: {
        type: "string",
        description: "The flow ID (GUID) to clone",
      },
      newDisplayName: {
        type: "string",
        description: "Display name for the cloned flow",
      },
      targetEnvironment: {
        type: "string",
        description: "Target environment for the clone. Defaults to same environment.",
      },
      sourceEnvironment: {
        type: "string",
        description: "Source environment. Uses default if omitted.",
      },
      updateConnections: {
        type: "object",
        description: "New connection references for the cloned flow",
        additionalProperties: {
          type: "object",
          properties: {
            connectionName: { type: "string" },
            id: { type: "string" },
            source: { type: "string", enum: ["Invoker", "Embedded"] },
            tier: { type: "string", enum: ["Standard", "Premium", "NotSpecified"] },
          },
          required: ["connectionName", "id"],
        },
      },
    },
    required: ["sourceFlowId", "newDisplayName"],
  },
};

export async function handleCloneFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = cloneFlowInputSchema.parse(input);

  logger.info(
    { sourceFlowId: parsed.sourceFlowId, newDisplayName: parsed.newDisplayName },
    "Executing clone_flow"
  );

  // Get the source flow
  const sourceFlow = await api.getFlow(parsed.sourceFlowId, parsed.sourceEnvironment);

  // structuredClone keeps the types a JSON round-trip would erase to `any`.
  const clonedDefinition = structuredClone(sourceFlow.properties.definition);
  const clonedConnRefs: Record<string, ConnectionReference> = structuredClone(
    sourceFlow.properties.connectionReferences ?? {},
  );

  // Update connection references if provided
  if (parsed.updateConnections) {
    for (const [key, newRef] of Object.entries(parsed.updateConnections)) {
      if (clonedConnRefs[key]) {
        clonedConnRefs[key] = {
          connectionName: newRef.connectionName,
          id: newRef.id,
          source: newRef.source ?? clonedConnRefs[key]?.source ?? "Embedded",
          tier: newRef.tier ?? clonedConnRefs[key]?.tier ?? "NotSpecified",
        };
      } else {
        // Add new connection reference
        clonedConnRefs[key] = {
          connectionName: newRef.connectionName,
          id: newRef.id,
          source: newRef.source ?? "Embedded",
          tier: newRef.tier ?? "NotSpecified",
        };
      }
    }
  }

  // Create the cloned flow
  const createRequest: CreateFlowRequest = {
    properties: {
      displayName: parsed.newDisplayName,
      definition: clonedDefinition,
      connectionReferences: clonedConnRefs,
    },
  };

  const targetEnv = parsed.targetEnvironment ?? parsed.sourceEnvironment;
  const clonedFlow = await api.createFlow(createRequest, targetEnv);

  // The create API brings flows up Started. A clone that starts running the
  // moment it exists is dangerous — a recurrence-triggered source means the
  // copy can fire against live systems within seconds (observed 2026-08-27:
  // a cloned 5-minute-recurrence flow ran production Jira/Entra calls in the
  // ~40s before it could be stopped by hand). Stop it to honor the documented
  // contract; if the stop fails, say so loudly instead of claiming Stopped.
  let state = clonedFlow.properties.state;
  let stopWarning: string | undefined;
  if (state !== "Stopped") {
    try {
      await api.stopFlow(clonedFlow.name, targetEnv);
      state = "Stopped";
    } catch (err) {
      stopWarning =
        `WARNING: the clone was created in ${state} state and could not be stopped ` +
        `(${err instanceof Error ? err.message : String(err)}). ` +
        `Its trigger is LIVE — stop it with toggle_flow before it fires.`;
      logger.warn({ flowId: clonedFlow.name, err }, "clone_flow: failed to stop cloned flow");
    }
  }

  const lines = [
    `Cloned "${sourceFlow.properties.displayName}" (${parsed.sourceFlowId}) as "${clonedFlow.properties.displayName}" (${clonedFlow.name}).`,
    `  State: ${state}${stopWarning ? "" : " — the clone is left stopped so its trigger cannot fire before you check it"}`,
    `  ${Object.keys(clonedDefinition.triggers).length} trigger, ${Object.keys(clonedDefinition.actions).length} actions, ${Object.keys(clonedConnRefs).length} connection references`,
  ];
  if (stopWarning) lines.push(`  ⚠️ ${stopWarning}`);
  lines.push(
    Object.keys(clonedConnRefs).length > 0
      ? `Next: check its connections (list_connections), then toggle_flow with action "start" when it is ready.`
      : `Next: toggle_flow with action "start" when it is ready.`,
  );
  return lines.join("\n");

}
